import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { validateMeetingPointName, validatePhone, validateParticipants, validateAmount, validateRequiredFields } from '../utils/validation';
import { filterByMeetingPoint, filterByOptions, countParticipants } from '../utils/filters';
import { BOOKING_OPTIONS, formatAmount } from '../utils/formatting';
import BookingOptionBadge from '../components/BookingOptionBadge';

function Groups() {
  const { user, isAdmin, isGuide } = useAuth();
  const [groups, setGroups] = useState([]);
  const [guides, setGuides] = useState([]);
  const [pasteData, setPasteData] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedGroup, setExpandedGroup] = useState(null);

  // Edit group state
  const [editingGroup, setEditingGroup] = useState(null);
  const [editGroupForm, setEditGroupForm] = useState({ time_slot: '', pickup_point: '', group_number: '' });

  // Add booking state
  const [addingBookingGroup, setAddingBookingGroup] = useState(null);
  const [addBookingForm, setAddBookingForm] = useState({
    reference: '', first_name: '', surname: '', phone: '', email: '',
    participants: 1, language: 'English', meeting_point_id: '', notes: '',
    options: [], amount: ''
  });

  // Edit booking state
  const [editingBooking, setEditingBooking] = useState(null);
  const [editBookingForm, setEditBookingForm] = useState({
    reference: '', first_name: '', surname: '', phone: '', email: '', participants: 1,
    language: 'English', meeting_point_id: '', notes: '', options: [], amount: ''
  });

  // Form validation errors
  const [formErrors, setFormErrors] = useState({});

  // Meeting point state
  const [meetingPointInputs, setMeetingPointInputs] = useState({});
  const [meetingPointErrors, setMeetingPointErrors] = useState({});

  // Meeting point filter state (per group)
  const [meetingPointFilters, setMeetingPointFilters] = useState({});

  // Option filter state (global)
  const [selectedOptionFilters, setSelectedOptionFilters] = useState([]);

  useEffect(() => { loadGroups(); loadGuides(); }, [date]);

  const loadGroups = async () => {
    setLoading(true);
    const data = await api.getGroups(date);
    if (data && data.groups) setGroups(data.groups);
    setLoading(false);
  };

  const loadGuides = async () => {
    const data = await api.getGuides();
    if (data && data.guides) setGuides(data.guides.filter(g => g.role === 'guide' || !g.role));
  };

  const handleImport = async () => {
    if (!pasteData.trim()) return;
    setImporting(true);
    setImportResult(null);
    const result = await api.importGroups(pasteData);
    setImporting(false);
    if (result && !result.error) {
      setImportResult({ success: true, msg: `${result.groups_created} groupe(s), ${result.bookings_imported} réservations` });
      setPasteData('');
      loadGroups();
    } else {
      setImportResult({ success: false, msg: result?.error || 'Erreur' });
    }
  };

  const handleAssign = async (groupId, guideId) => {
    await api.assignGuide(groupId, parseInt(guideId) || 0);
    loadGroups();
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Supprimer ce groupe et toutes ses réservations ?')) return;
    await api.request(`groups.php?action=delete&id=${groupId}`, { method: 'DELETE' });
    loadGroups();
  };

  const handleRemoveBooking = async (bookingId) => {
    if (!window.confirm('Retirer cette réservation du groupe ?')) return;
    await api.request('groups.php?action=remove-booking', { method: 'POST', body: { booking_id: bookingId } });
    loadGroups();
  };

  // Edit Group
  const openEditGroup = (group) => {
    setEditingGroup(group.id);
    setEditGroupForm({
      time_slot: group.time_slot || '',
      pickup_point: group.pickup_point || '',
      group_number: group.group_number || ''
    });
  };

  const handleUpdateGroup = async () => {
    await api.request('groups.php?action=update', {
      method: 'POST',
      body: { group_id: editingGroup, ...editGroupForm }
    });
    setEditingGroup(null);
    loadGroups();
  };

  // Add Booking
  const openAddBooking = (groupId) => {
    setAddingBookingGroup(groupId);
    setFormErrors({});
    setAddBookingForm({
      reference: '', first_name: '', surname: '', phone: '', email: '',
      participants: 1, language: 'English', meeting_point_id: '', notes: '',
      options: [], amount: ''
    });
  };

  const handleAddBooking = async () => {
    const errors = {};
    if (!addBookingForm.first_name.trim()) {
      errors.first_name = 'Ce champ est obligatoire';
    }
    if (!addBookingForm.language || !addBookingForm.language.trim()) {
      errors.language = 'Ce champ est obligatoire';
    }
    if (!validateParticipants(addBookingForm.participants)) {
      errors.participants = 'Entre 1 et 200';
    }
    if (addBookingForm.phone && addBookingForm.phone.trim() && !validatePhone(addBookingForm.phone)) {
      errors.phone = 'Format: +XXX XXXXXXX';
    }
    if (addBookingForm.amount && addBookingForm.amount !== '' && !validateAmount(addBookingForm.amount)) {
      errors.amount = 'Entre 1 et 999 999';
    }
    if (!addBookingForm.meeting_point_id) {
      errors.meeting_point_id = 'Ce champ est obligatoire';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    const body = {
      group_id: addingBookingGroup,
      ...addBookingForm,
      options: addBookingForm.options.filter(o => o.selected).map(o => ({ label: o.label, quantity: o.quantity }))
    };
    // If amount is set, also add to extras
    if (addBookingForm.amount && parseInt(addBookingForm.amount, 10) > 0) {
      const group = groups.find(g => g.id === addingBookingGroup);
      const guideName = group?.guide_name || user?.name || '';
      await api.addExtra(
        group?.guide_id || user?.id,
        parseInt(addBookingForm.amount, 10),
        'Booking option amount',
        addBookingForm.reference,
        date
      );
    }
    await api.request('groups.php?action=add-booking', { method: 'POST', body });
    setAddingBookingGroup(null);
    loadGroups();
  };

  // Edit Booking
  const openEditBooking = (booking) => {
    setEditingBooking(booking.id);
    setFormErrors({});
    const existingOptions = BOOKING_OPTIONS.map(opt => {
      const found = (booking.options || []).find(
        bo => bo.label.toLowerCase() === opt.label.toLowerCase()
      );
      return {
        value: opt.value,
        label: opt.label,
        selected: !!found,
        quantity: found ? found.quantity : 1
      };
    });
    setEditBookingForm({
      reference: booking.reference || '',
      first_name: booking.first_name || '',
      surname: booking.surname || '',
      phone: booking.phone || '',
      email: booking.email || '',
      participants: booking.participants || 1,
      language: booking.language || 'English',
      meeting_point_id: booking.meeting_point_id || '',
      notes: booking.notes || '',
      options: existingOptions,
      amount: booking.amount || ''
    });
  };

  const handleUpdateBooking = async () => {
    const errors = {};
    if (!editBookingForm.first_name.trim()) {
      errors.first_name = 'Ce champ est obligatoire';
    }
    if (!editBookingForm.language || !editBookingForm.language.trim()) {
      errors.language = 'Ce champ est obligatoire';
    }
    if (!validateParticipants(editBookingForm.participants)) {
      errors.participants = 'Entre 1 et 200';
    }
    if (editBookingForm.phone && editBookingForm.phone.trim() && !validatePhone(editBookingForm.phone)) {
      errors.phone = 'Format: +XXX XXXXXXX';
    }
    if (editBookingForm.amount && editBookingForm.amount !== '' && !validateAmount(editBookingForm.amount)) {
      errors.amount = 'Entre 1 et 999 999';
    }
    if (!editBookingForm.meeting_point_id) {
      errors.meeting_point_id = 'Ce champ est obligatoire';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    const body = {
      booking_id: editingBooking,
      ...editBookingForm,
      options: editBookingForm.options.filter(o => o.selected).map(o => ({ label: o.label, quantity: o.quantity }))
    };
    // If amount is set, also add to extras
    if (editBookingForm.amount && parseInt(editBookingForm.amount, 10) > 0) {
      const group = groups.find(g => g.bookings && g.bookings.some(b => b.id === editingBooking));
      const guideName = group?.guide_name || user?.name || '';
      await api.addExtra(
        group?.guide_id || user?.id,
        parseInt(editBookingForm.amount, 10),
        'Booking option amount',
        editBookingForm.reference,
        date
      );
    }
    await api.request('groups.php?action=update-booking', { method: 'POST', body });
    setEditingBooking(null);
    loadGroups();
  };

  // Meeting Points
  const handleAddMeetingPoint = async (groupId) => {
    const name = (meetingPointInputs[groupId] || '').trim();
    const group = groups.find(g => g.id === groupId);
    const existingPoints = group?.meeting_points || [];

    if (!validateMeetingPointName(name)) {
      setMeetingPointErrors({ ...meetingPointErrors, [groupId]: 'Maximum 60 caractères' });
      return;
    }

    const isDuplicate = existingPoints.some(
      mp => mp.name.toLowerCase() === name.toLowerCase()
    );
    if (isDuplicate) {
      setMeetingPointErrors({ ...meetingPointErrors, [groupId]: 'Ce point existe déjà' });
      return;
    }

    setMeetingPointErrors({ ...meetingPointErrors, [groupId]: '' });
    await api.addMeetingPoint(groupId, name);
    setMeetingPointInputs({ ...meetingPointInputs, [groupId]: '' });
    loadGroups();
  };

  const handleRemoveMeetingPoint = async (meetingPointId) => {
    await api.removeMeetingPoint(meetingPointId);
    loadGroups();
  };

  // Option filter toggle
  const toggleOptionFilter = (optionValue) => {
    setSelectedOptionFilters(prev =>
      prev.includes(optionValue)
        ? prev.filter(v => v !== optionValue)
        : [...prev, optionValue]
    );
  };

  // Toggle booking option in add form
  const toggleAddBookingOption = (optValue) => {
    setAddBookingForm(prev => {
      const existing = prev.options.find(o => o.value === optValue);
      if (existing) {
        return { ...prev, options: prev.options.map(o => o.value === optValue ? { ...o, selected: !o.selected } : o) };
      }
      const opt = BOOKING_OPTIONS.find(o => o.value === optValue);
      return { ...prev, options: [...prev.options, { value: optValue, label: opt.label, selected: true, quantity: 1 }] };
    });
  };

  // Toggle booking option in edit form
  const toggleEditBookingOption = (optValue) => {
    setEditBookingForm(prev => ({
      ...prev,
      options: prev.options.map(o => o.value === optValue ? { ...o, selected: !o.selected } : o)
    }));
  };

  // Get filtered bookings for a group
  const getFilteredBookings = (group) => {
    let bookings = group.bookings || [];
    // Apply option filter (global)
    if (selectedOptionFilters.length > 0) {
      bookings = filterByOptions(bookings, selectedOptionFilters);
    }
    // Apply meeting point filter (per group)
    const mpFilter = meetingPointFilters[group.id];
    if (mpFilter) {
      bookings = filterByMeetingPoint(bookings, mpFilter);
    }
    return bookings;
  };

  // Group bookings by meeting point, sorted alphabetically
  const groupBookingsByMeetingPoint = (bookings, meetingPoints) => {
    const sorted = [...(meetingPoints || [])].sort((a, b) => a.name.localeCompare(b.name));
    const grouped = sorted.map(mp => ({
      meetingPoint: mp,
      bookings: bookings.filter(b => Number(b.meeting_point_id) === Number(mp.id))
    }));
    // Also include bookings with no meeting point assigned
    const unassigned = bookings.filter(b => !b.meeting_point_id || !meetingPoints?.some(mp => Number(mp.id) === Number(b.meeting_point_id)));
    if (unassigned.length > 0) {
      grouped.push({ meetingPoint: { id: null, name: 'Non assigné' }, bookings: unassigned });
    }
    return grouped;
  };

  const totalParticipants = groups.reduce((s, g) => s + (g.total_participants || 0), 0);

  // Render booking options multi-select for forms
  const renderOptionsSelect = (options, toggleFn, setFormFn, formState, formKey) => {
    return (
      <div style={{ marginBottom: '6px' }}>
        <div style={{ fontSize: '11px', color: '#A78BFA', marginBottom: '4px', fontWeight: 600 }}>Options</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
          {BOOKING_OPTIONS.map(opt => {
            const current = options.find(o => o.value === opt.value);
            const isSelected = current?.selected;
            return (
              <button key={opt.value} type="button" onClick={() => toggleFn(opt.value)} style={{
                padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 500, cursor: 'pointer',
                border: isSelected ? '1px solid #7C3AED' : '1px solid #444',
                background: isSelected ? '#2D1B69' : '#2a2a2a',
                color: isSelected ? '#C4B5FD' : '#888'
              }}>{opt.label}</button>
            );
          })}
        </div>
        {/* Quantity selectors for selected options */}
        {options.filter(o => o.selected).map(opt => (
          <div key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <span style={{ fontSize: '11px', color: '#ccc', minWidth: '100px' }}>{opt.label}</span>
            <span style={{ fontSize: '10px', color: '#888' }}>Qté:</span>
            <input
              type="number" min="1" max="10" value={opt.quantity}
              onChange={e => {
                const qty = Math.min(10, Math.max(1, parseInt(e.target.value) || 1));
                setFormFn(prev => ({
                  ...prev,
                  [formKey]: prev[formKey].map(o => o.value === opt.value ? { ...o, quantity: qty } : o)
                }));
              }}
              style={{ ...inputStyle, width: '50px', padding: '4px 8px', fontSize: '11px' }}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="animate-in">
      {/* Import Section */}
      <div style={{ background: '#1a1a1a', borderRadius: '16px', padding: '16px', marginBottom: '16px', border: '1px solid #333' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ fontWeight: 600, fontSize: '14px' }}>📋 Importer un groupe</span>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            style={{ background: '#2a2a2a', border: '1px solid #444', borderRadius: '8px', padding: '6px 10px', color: 'white', fontSize: '12px' }} />
        </div>
        <textarea
          value={pasteData}
          onChange={e => setPasteData(e.target.value)}
          placeholder={"Collez les données du groupe ici...\n\nFormat accepté:\n📅 Date: 22/5/26\n13:00\n*Arsat Moulay Abdesalam*\n📞 447563... | 🔹 Ref: GYG...\n👤 Name: ...\n📧 Email: ...\n🗣 Lang: English | 🎫 2P"}
          style={{
            width: '100%', minHeight: '140px', background: '#2a2a2a', border: '2px dashed #444',
            borderRadius: '12px', padding: '12px', color: 'white', fontSize: '11px',
            fontFamily: 'monospace', resize: 'vertical', outline: 'none'
          }}
        />
        <button onClick={handleImport} disabled={importing} style={{
          width: '100%', marginTop: '10px', padding: '12px', background: importing ? '#555' : '#E53E3E',
          color: 'white', border: 'none', borderRadius: '10px', fontWeight: 600, fontSize: '14px', cursor: 'pointer'
        }}>
          {importing ? 'Import en cours...' : 'Importer comme 1 groupe'}
        </button>
        {importResult && (
          <div style={{
            marginTop: '8px', padding: '10px', borderRadius: '8px', fontSize: '13px',
            background: importResult.success ? '#064E3B' : '#7F1D1D',
            color: importResult.success ? '#6EE7B7' : '#FCA5A5'
          }}>
            {importResult.success ? '✓' : '✗'} {importResult.msg}
          </div>
        )}
      </div>

      {/* Option Filter Section */}
      <div style={{ background: '#1a1a1a', borderRadius: '12px', padding: '12px', marginBottom: '12px', border: '1px solid #333' }}>
        <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '8px', color: '#A78BFA' }}>🔍 Filtrer par options</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {BOOKING_OPTIONS.map(opt => {
            const isActive = selectedOptionFilters.includes(opt.value);
            return (
              <button key={opt.value} onClick={() => toggleOptionFilter(opt.value)} style={{
                padding: '5px 12px', borderRadius: '14px', fontSize: '11px', fontWeight: 500, cursor: 'pointer',
                border: isActive ? '1px solid #7C3AED' : '1px solid #444',
                background: isActive ? '#2D1B69' : '#2a2a2a',
                color: isActive ? '#C4B5FD' : '#888'
              }}>{opt.label}</button>
            );
          })}
          {selectedOptionFilters.length > 0 && (
            <button onClick={() => setSelectedOptionFilters([])} style={{
              padding: '5px 12px', borderRadius: '14px', fontSize: '11px', fontWeight: 500, cursor: 'pointer',
              border: '1px solid #555', background: '#333', color: '#aaa'
            }}>✕ Effacer</button>
          )}
        </div>
      </div>

      {/* Groups Summary */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ fontWeight: 700, fontSize: '16px' }}>Groupes du {date}</h3>
        <div style={{ fontSize: '12px', color: '#888' }}>
          {groups.length} groupes · {totalParticipants}P
        </div>
      </div>

      {/* Groups List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '30px' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
      ) : groups.length === 0 ? (
        <div style={{ background: '#1a1a1a', borderRadius: '16px', padding: '30px', textAlign: 'center', border: '1px solid #333' }}>
          <div style={{ color: '#555' }}>Aucun groupe pour cette date</div>
        </div>
      ) : (
        groups.map((group, idx) => {
          const filteredBookings = getFilteredBookings(group);
          const meetingPointSections = groupBookingsByMeetingPoint(filteredBookings, group.meeting_points);
          const mpFilter = meetingPointFilters[group.id];

          return (
          <div key={group.id} style={{
            background: '#1a1a1a', borderRadius: '12px', padding: '14px', marginBottom: '10px',
            borderLeft: `4px solid ${group.guide_id ? '#10B981' : '#EF4444'}`,
            border: '1px solid #333'
          }}>
            {/* Group Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                 onClick={() => setExpandedGroup(expandedGroup === group.id ? null : group.id)}>
              <div style={{ cursor: 'pointer', flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: 700, fontSize: '14px' }}>Groupe {group.group_number || idx + 1}</span>
                  <span style={{ background: '#1E3A5F', color: '#60A5FA', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600 }}>
                    {group.total_participants}P
                  </span>
                  <span style={{ fontSize: '11px', color: '#888' }}>🕐 {group.time_slot?.substring(0,5)}</span>
                </div>
                <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                  📍 {group.pickup_point}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {group.guide_name && (
                  <span style={{ background: '#064E3B', color: '#6EE7B7', padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600 }}>
                    {group.guide_name}
                  </span>
                )}
                <span style={{ color: '#555', fontSize: '16px' }}>{expandedGroup === group.id ? '▲' : '▼'}</span>
              </div>
            </div>

            {/* Assign Guide + Actions */}
            <div style={{ marginTop: '10px', display: 'flex', gap: '6px' }}>
              <select value={group.guide_id || ''} onChange={e => handleAssign(group.id, e.target.value)}
                style={{
                  flex: 1, padding: '8px', background: '#2a2a2a', border: '1px solid #444',
                  borderRadius: '8px', color: 'white', fontSize: '12px', outline: 'none'
                }}>
                <option value="">-- Assigner guide --</option>
                {guides.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              {isAdmin && (
                <button onClick={(e) => { e.stopPropagation(); openEditGroup(group); }} style={{
                  background: '#1E3A5F', border: '1px solid #2563EB', color: '#60A5FA',
                  borderRadius: '8px', padding: '8px 10px', fontSize: '11px', cursor: 'pointer'
                }}>✏️</button>
              )}
              {(isAdmin || isGuide) && (group.meeting_points && group.meeting_points.length > 0) && (
                <button onClick={(e) => { e.stopPropagation(); openAddBooking(group.id); }} style={{
                  background: '#064E3B', border: '1px solid #10B981', color: '#6EE7B7',
                  borderRadius: '8px', padding: '8px 10px', fontSize: '11px', cursor: 'pointer'
                }}>+📋</button>
              )}
              {isAdmin && (
                <button onClick={() => handleDeleteGroup(group.id)} style={{
                  background: '#3B1111', border: '1px solid #7F1D1D', color: '#FCA5A5',
                  borderRadius: '8px', padding: '8px 10px', fontSize: '11px', cursor: 'pointer'
                }}>🗑</button>
              )}
            </div>

            {/* No meeting points warning */}
            {(!group.meeting_points || group.meeting_points.length === 0) && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#F59E0B', fontStyle: 'italic' }}>
                ⚠️ Ajoutez un point de rencontre d'abord
              </div>
            )}

            {/* Meeting Points Section */}
            {(isAdmin || isGuide) && (
              <div style={{ marginTop: '10px', padding: '10px', background: '#222', borderRadius: '10px', border: '1px solid #333' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '8px', color: '#A78BFA' }}>📍 Points de rencontre</div>
                
                {/* Existing meeting points as tags */}
                {group.meeting_points && group.meeting_points.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                    {group.meeting_points.map(mp => (
                      <span key={mp.id} style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        background: '#1E3A5F', color: '#93C5FD', padding: '4px 10px',
                        borderRadius: '12px', fontSize: '11px', fontWeight: 500
                      }}>
                        {mp.name}
                        {isAdmin && (
                          <button
                            onClick={() => handleRemoveMeetingPoint(mp.id)}
                            style={{
                              background: 'none', border: 'none', color: '#FCA5A5',
                              cursor: 'pointer', fontSize: '12px', padding: '0 2px',
                              lineHeight: 1
                            }}
                            aria-label={`Supprimer ${mp.name}`}
                          >✕</button>
                        )}
                      </span>
                    ))}
                  </div>
                )}

                {/* Add meeting point input */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input
                    value={meetingPointInputs[group.id] || ''}
                    onChange={e => {
                      setMeetingPointInputs({ ...meetingPointInputs, [group.id]: e.target.value });
                      if (meetingPointErrors[group.id]) {
                        setMeetingPointErrors({ ...meetingPointErrors, [group.id]: '' });
                      }
                    }}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddMeetingPoint(group.id); }}
                    placeholder="Nom du point de rencontre"
                    maxLength={60}
                    style={{ ...inputStyle, flex: 1, padding: '8px 10px', fontSize: '12px' }}
                  />
                  <button
                    onClick={() => handleAddMeetingPoint(group.id)}
                    style={{
                      background: '#7C3AED', border: 'none', color: 'white',
                      borderRadius: '8px', padding: '8px 12px', fontSize: '11px',
                      fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap'
                    }}
                  >Ajouter</button>
                </div>
                {meetingPointErrors[group.id] && (
                  <div style={{ fontSize: '11px', color: '#FCA5A5', marginTop: '4px' }}>
                    {meetingPointErrors[group.id]}
                  </div>
                )}
              </div>
            )}

            {/* Edit Group Form (inline) */}
            {editingGroup === group.id && (
              <div style={{ marginTop: '12px', padding: '12px', background: '#222', borderRadius: '10px', border: '1px solid #444' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', color: '#60A5FA' }}>✏️ Modifier le groupe</div>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                  <input value={editGroupForm.time_slot} onChange={e => setEditGroupForm({...editGroupForm, time_slot: e.target.value})}
                    placeholder="Heure (ex: 13:00)" style={{...inputStyle, flex: 1}} />
                  <input value={editGroupForm.group_number} onChange={e => setEditGroupForm({...editGroupForm, group_number: e.target.value})}
                    placeholder="N° groupe" style={{...inputStyle, width: '80px'}} />
                </div>
                <input value={editGroupForm.pickup_point} onChange={e => setEditGroupForm({...editGroupForm, pickup_point: e.target.value})}
                  placeholder="Point de ramassage" style={{...inputStyle, marginBottom: '8px'}} />
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={handleUpdateGroup} style={{
                    flex: 1, padding: '10px', background: '#10B981', color: 'white',
                    border: 'none', borderRadius: '10px', fontWeight: 600, fontSize: '12px', cursor: 'pointer'
                  }}>Enregistrer</button>
                  <button onClick={() => setEditingGroup(null)} style={{
                    padding: '10px 14px', background: '#333', color: '#aaa',
                    border: 'none', borderRadius: '10px', fontSize: '12px', cursor: 'pointer'
                  }}>Annuler</button>
                </div>
              </div>
            )}

            {/* Add Booking Form (inline) */}
            {addingBookingGroup === group.id && (
              <div style={{ marginTop: '12px', padding: '12px', background: '#222', borderRadius: '10px', border: '1px solid #444' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', color: '#6EE7B7' }}>➕ Ajouter une réservation</div>
                <input value={addBookingForm.reference} onChange={e => setAddBookingForm({...addBookingForm, reference: e.target.value})}
                  placeholder="Référence (ex: GYG-123)" style={{...inputStyle, marginBottom: '6px'}} />
                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                  <div style={{ flex: 1 }}>
                    <input value={addBookingForm.first_name} onChange={e => { setAddBookingForm({...addBookingForm, first_name: e.target.value}); if (formErrors.first_name) setFormErrors(prev => ({...prev, first_name: ''})); }}
                      placeholder="Prénom *" style={{...inputStyle, ...(formErrors.first_name ? {border: '1px solid #E53E3E'} : {})}} />
                    {formErrors.first_name && <div style={{ fontSize: '11px', color: '#FCA5A5', marginTop: '2px' }}>{formErrors.first_name}</div>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <input value={addBookingForm.surname} onChange={e => setAddBookingForm({...addBookingForm, surname: e.target.value})}
                      placeholder="Nom" style={{...inputStyle}} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                  <div style={{ flex: 1 }}>
                    <input value={addBookingForm.phone} onChange={e => { setAddBookingForm({...addBookingForm, phone: e.target.value}); if (formErrors.phone) setFormErrors(prev => ({...prev, phone: ''})); }}
                      placeholder="Téléphone" style={{...inputStyle, ...(formErrors.phone ? {border: '1px solid #E53E3E'} : {})}} />
                    {formErrors.phone && <div style={{ fontSize: '11px', color: '#FCA5A5', marginTop: '2px' }}>{formErrors.phone}</div>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <input value={addBookingForm.email} onChange={e => setAddBookingForm({...addBookingForm, email: e.target.value})}
                      placeholder="Email" style={{...inputStyle}} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                  <div style={{ width: '80px' }}>
                    <input type="number" min="1" value={addBookingForm.participants}
                      onChange={e => { setAddBookingForm({...addBookingForm, participants: parseInt(e.target.value) || 1}); if (formErrors.participants) setFormErrors(prev => ({...prev, participants: ''})); }}
                      placeholder="Participants" style={{...inputStyle, ...(formErrors.participants ? {border: '1px solid #E53E3E'} : {})}} />
                    {formErrors.participants && <div style={{ fontSize: '11px', color: '#FCA5A5', marginTop: '2px' }}>{formErrors.participants}</div>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <select value={addBookingForm.language} onChange={e => { setAddBookingForm({...addBookingForm, language: e.target.value}); if (formErrors.language) setFormErrors(prev => ({...prev, language: ''})); }}
                      style={{...inputStyle, ...(formErrors.language ? {border: '1px solid #E53E3E'} : {})}}>
                      <option value="English">English</option>
                      <option value="French">Français</option>
                      <option value="Spanish">Español</option>
                      <option value="German">Deutsch</option>
                      <option value="Italian">Italiano</option>
                      <option value="Arabic">العربية</option>
                    </select>
                    {formErrors.language && <div style={{ fontSize: '11px', color: '#FCA5A5', marginTop: '2px' }}>{formErrors.language}</div>}
                  </div>
                </div>
                {/* Meeting point dropdown (required) */}
                <div style={{ marginBottom: '6px' }}>
                  <select value={addBookingForm.meeting_point_id}
                    onChange={e => { setAddBookingForm({...addBookingForm, meeting_point_id: e.target.value}); if (formErrors.meeting_point_id) setFormErrors(prev => ({...prev, meeting_point_id: ''})); }}
                    style={{...inputStyle, ...(formErrors.meeting_point_id ? {border: '1px solid #E53E3E'} : {})}}>
                    <option value="">-- Point de rencontre * --</option>
                    {(group.meeting_points || []).map(mp => (
                      <option key={mp.id} value={mp.id}>{mp.name}</option>
                    ))}
                  </select>
                  {formErrors.meeting_point_id && <div style={{ fontSize: '11px', color: '#FCA5A5', marginTop: '2px' }}>{formErrors.meeting_point_id}</div>}
                </div>

                {/* Booking options multi-select */}
                {renderOptionsSelect(
                  addBookingForm.options,
                  toggleAddBookingOption,
                  setAddBookingForm,
                  addBookingForm,
                  'options'
                )}

                {/* Amount field */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                  <div style={{ flex: 1 }}>
                    <input type="number" min="0" value={addBookingForm.amount}
                      onChange={e => { setAddBookingForm({...addBookingForm, amount: e.target.value}); if (formErrors.amount) setFormErrors(prev => ({...prev, amount: ''})); }}
                      placeholder="Montant (optionnel)"
                      style={{...inputStyle, ...(formErrors.amount ? {border: '1px solid #E53E3E'} : {})}} />
                    {formErrors.amount && <div style={{ fontSize: '11px', color: '#FCA5A5', marginTop: '2px' }}>{formErrors.amount}</div>}
                  </div>
                  {addBookingForm.amount && parseInt(addBookingForm.amount, 10) > 0 && (
                    <span style={{ fontSize: '12px', color: '#10B981', fontWeight: 600 }}>
                      {formatAmount(addBookingForm.amount)}
                    </span>
                  )}
                </div>

                <textarea value={addBookingForm.notes} onChange={e => setAddBookingForm({...addBookingForm, notes: e.target.value})}
                  placeholder="Notes (optionnel)" rows={2}
                  style={{...inputStyle, marginBottom: '8px', resize: 'vertical'}} />
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={handleAddBooking} style={{
                    flex: 1, padding: '10px', background: '#10B981', color: 'white',
                    border: 'none', borderRadius: '10px', fontWeight: 600, fontSize: '12px', cursor: 'pointer'
                  }}>Ajouter</button>
                  <button onClick={() => setAddingBookingGroup(null)} style={{
                    padding: '10px 14px', background: '#333', color: '#aaa',
                    border: 'none', borderRadius: '10px', fontSize: '12px', cursor: 'pointer'
                  }}>Annuler</button>
                </div>
              </div>
            )}

            {/* Expanded: Meeting Point Filter + Bookings by Section */}
            {expandedGroup === group.id && (
              <div style={{ marginTop: '12px', borderTop: '1px solid #333', paddingTop: '10px' }}>
                {/* Meeting point filter dropdown */}
                {group.meeting_points && group.meeting_points.length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <select
                      value={mpFilter || ''}
                      onChange={e => setMeetingPointFilters({ ...meetingPointFilters, [group.id]: e.target.value || null })}
                      style={{ ...inputStyle, padding: '6px 10px', fontSize: '11px' }}
                    >
                      <option value="">Tous les points de rencontre</option>
                      {[...(group.meeting_points || [])].sort((a, b) => a.name.localeCompare(b.name)).map(mp => (
                        <option key={mp.id} value={mp.id}>{mp.name} ({countParticipants(filterByMeetingPoint(group.bookings || [], mp.id))}P)</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Bookings grouped by meeting point */}
                {meetingPointSections.map(section => (
                  section.bookings.length > 0 && (
                    <div key={section.meetingPoint.id || 'unassigned'} style={{ marginBottom: '12px' }}>
                      {/* Meeting point section header */}
                      <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '6px 10px', background: '#1E3A5F', borderRadius: '8px', marginBottom: '6px'
                      }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#93C5FD' }}>
                          📍 {section.meetingPoint.name}
                        </span>
                        <span style={{ fontSize: '11px', color: '#60A5FA', fontWeight: 500 }}>
                          {countParticipants(section.bookings)}P
                        </span>
                      </div>

                      {/* Bookings in this section */}
                      {section.bookings.map(b => (
                        <div key={b.id} style={{ padding: '8px 0', borderBottom: '1px solid #2a2a2a' }}>
                          {editingBooking === b.id ? (
                            /* Edit Booking Form */
                            <div style={{ padding: '10px', background: '#222', borderRadius: '8px', border: '1px solid #444' }}>
                              <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '6px', color: '#60A5FA' }}>✏️ Modifier réservation</div>
                              {/* Reference field - read-only for Guide */}
                              <input value={editBookingForm.reference}
                                onChange={e => !isGuide && setEditBookingForm({...editBookingForm, reference: e.target.value})}
                                readOnly={isGuide}
                                placeholder="Référence"
                                style={{...inputStyle, marginBottom: '6px', padding: '8px 10px', fontSize: '12px',
                                  opacity: isGuide ? 0.6 : 1, cursor: isGuide ? 'not-allowed' : 'text'}} />
                              <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                                <div style={{ flex: 1 }}>
                                  <input value={editBookingForm.first_name} onChange={e => { setEditBookingForm({...editBookingForm, first_name: e.target.value}); if (formErrors.first_name) setFormErrors(prev => ({...prev, first_name: ''})); }}
                                    placeholder="Prénom" style={{...inputStyle, padding: '8px 10px', fontSize: '12px', ...(formErrors.first_name ? {border: '1px solid #E53E3E'} : {})}} />
                                  {formErrors.first_name && <div style={{ fontSize: '11px', color: '#FCA5A5', marginTop: '2px' }}>{formErrors.first_name}</div>}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <input value={editBookingForm.surname} onChange={e => setEditBookingForm({...editBookingForm, surname: e.target.value})}
                                    placeholder="Nom" style={{...inputStyle, padding: '8px 10px', fontSize: '12px'}} />
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                                <div style={{ flex: 1 }}>
                                  <input value={editBookingForm.phone} onChange={e => { setEditBookingForm({...editBookingForm, phone: e.target.value}); if (formErrors.phone) setFormErrors(prev => ({...prev, phone: ''})); }}
                                    placeholder="Téléphone" style={{...inputStyle, padding: '8px 10px', fontSize: '12px', ...(formErrors.phone ? {border: '1px solid #E53E3E'} : {})}} />
                                  {formErrors.phone && <div style={{ fontSize: '11px', color: '#FCA5A5', marginTop: '2px' }}>{formErrors.phone}</div>}
                                </div>
                                <div style={{ width: '60px' }}>
                                  <input type="number" min="1" value={editBookingForm.participants}
                                    onChange={e => { setEditBookingForm({...editBookingForm, participants: parseInt(e.target.value) || 1}); if (formErrors.participants) setFormErrors(prev => ({...prev, participants: ''})); }}
                                    placeholder="P" style={{...inputStyle, padding: '8px 10px', fontSize: '12px', ...(formErrors.participants ? {border: '1px solid #E53E3E'} : {})}} />
                                  {formErrors.participants && <div style={{ fontSize: '11px', color: '#FCA5A5', marginTop: '2px' }}>{formErrors.participants}</div>}
                                </div>
                              </div>
                              {/* Meeting point dropdown in edit form */}
                              <div style={{ marginBottom: '6px' }}>
                                <select value={editBookingForm.meeting_point_id}
                                  onChange={e => { setEditBookingForm({...editBookingForm, meeting_point_id: e.target.value}); if (formErrors.meeting_point_id) setFormErrors(prev => ({...prev, meeting_point_id: ''})); }}
                                  style={{...inputStyle, padding: '8px 10px', fontSize: '12px', ...(formErrors.meeting_point_id ? {border: '1px solid #E53E3E'} : {})}}>
                                  <option value="">-- Point de rencontre --</option>
                                  {(group.meeting_points || []).map(mp => (
                                    <option key={mp.id} value={mp.id}>{mp.name}</option>
                                  ))}
                                </select>
                                {formErrors.meeting_point_id && <div style={{ fontSize: '11px', color: '#FCA5A5', marginTop: '2px' }}>{formErrors.meeting_point_id}</div>}
                              </div>

                              {/* Email and Language fields in edit form */}
                              <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                                <div style={{ flex: 1 }}>
                                  <input value={editBookingForm.email} onChange={e => setEditBookingForm({...editBookingForm, email: e.target.value})}
                                    placeholder="Email" style={{...inputStyle, padding: '8px 10px', fontSize: '12px'}} />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <select value={editBookingForm.language} onChange={e => { setEditBookingForm({...editBookingForm, language: e.target.value}); if (formErrors.language) setFormErrors(prev => ({...prev, language: ''})); }}
                                    style={{...inputStyle, padding: '8px 10px', fontSize: '12px', ...(formErrors.language ? {border: '1px solid #E53E3E'} : {})}}>
                                    <option value="English">English</option>
                                    <option value="French">Français</option>
                                    <option value="Spanish">Español</option>
                                    <option value="German">Deutsch</option>
                                    <option value="Italian">Italiano</option>
                                    <option value="Arabic">العربية</option>
                                  </select>
                                  {formErrors.language && <div style={{ fontSize: '11px', color: '#FCA5A5', marginTop: '2px' }}>{formErrors.language}</div>}
                                </div>
                              </div>

                              {/* Booking options multi-select in edit form */}
                              {renderOptionsSelect(
                                editBookingForm.options,
                                toggleEditBookingOption,
                                setEditBookingForm,
                                editBookingForm,
                                'options'
                              )}

                              {/* Amount field in edit form */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                <div style={{ flex: 1 }}>
                                  <input type="number" min="0" value={editBookingForm.amount}
                                    onChange={e => { setEditBookingForm({...editBookingForm, amount: e.target.value}); if (formErrors.amount) setFormErrors(prev => ({...prev, amount: ''})); }}
                                    placeholder="Montant (optionnel)"
                                    style={{...inputStyle, padding: '8px 10px', fontSize: '12px', ...(formErrors.amount ? {border: '1px solid #E53E3E'} : {})}} />
                                  {formErrors.amount && <div style={{ fontSize: '11px', color: '#FCA5A5', marginTop: '2px' }}>{formErrors.amount}</div>}
                                </div>
                                {editBookingForm.amount && parseInt(editBookingForm.amount, 10) > 0 && (
                                  <span style={{ fontSize: '11px', color: '#10B981', fontWeight: 600 }}>
                                    {formatAmount(editBookingForm.amount)}
                                  </span>
                                )}
                              </div>

                              <textarea value={editBookingForm.notes} onChange={e => setEditBookingForm({...editBookingForm, notes: e.target.value})}
                                placeholder="Notes" rows={2}
                                style={{...inputStyle, marginBottom: '6px', padding: '8px 10px', fontSize: '12px', resize: 'vertical'}} />
                              <div style={{ display: 'flex', gap: '6px' }}>
                                <button onClick={handleUpdateBooking} style={{
                                  flex: 1, padding: '8px', background: '#10B981', color: 'white',
                                  border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '11px', cursor: 'pointer'
                                }}>Enregistrer</button>
                                <button onClick={() => setEditingBooking(null)} style={{
                                  padding: '8px 12px', background: '#333', color: '#aaa',
                                  border: 'none', borderRadius: '8px', fontSize: '11px', cursor: 'pointer'
                                }}>Annuler</button>
                              </div>
                            </div>
                          ) : (
                            /* Booking Display */
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '13px', fontWeight: 500 }}>
                                  {b.first_name} {b.surname}
                                  <span style={{ color: '#888', fontWeight: 400 }}> · {b.participants}P · {b.language}</span>
                                </div>
                                <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                                  {b.reference} · 📍 {b.meeting_point_name || b.pickup_point}
                                  {b.phone && <span> · 📞 {b.phone}</span>}
                                  {b.notes && <span> · 📝 {b.notes}</span>}
                                </div>
                                {/* Booking option badges */}
                                {b.options && b.options.length > 0 && (
                                  <div style={{ marginTop: '4px' }}>
                                    {b.options.map((opt, i) => (
                                      <BookingOptionBadge key={i} option={opt} />
                                    ))}
                                  </div>
                                )}
                                {/* Amount display */}
                                {b.amount && (
                                  <div style={{ fontSize: '11px', color: '#10B981', fontWeight: 600, marginTop: '2px' }}>
                                    {formatAmount(b.amount)}
                                  </div>
                                )}
                              </div>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                {b.phone && (
                                  <a href={`https://wa.me/${b.phone.replace(/[^0-9]/g,'')}`} target="_blank" rel="noreferrer"
                                    style={{ background: '#25D366', color: 'white', borderRadius: '6px', padding: '4px 6px', fontSize: '10px', textDecoration: 'none' }}>
                                    WA
                                  </a>
                                )}
                                {(isAdmin || isGuide) && (
                                  <button onClick={() => openEditBooking(b)} style={{
                                    background: '#1E3A5F', border: 'none', color: '#60A5FA',
                                    borderRadius: '6px', padding: '4px 6px', fontSize: '10px', cursor: 'pointer'
                                  }}>✏️</button>
                                )}
                                {isAdmin && (
                                  <button onClick={() => handleRemoveBooking(b.id)} style={{
                                    background: '#3B1111', border: 'none', color: '#FCA5A5',
                                    borderRadius: '6px', padding: '4px 6px', fontSize: '10px', cursor: 'pointer'
                                  }}>✗</button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )
                ))}

                <div style={{ fontSize: '11px', color: '#555', marginTop: '8px', textAlign: 'center' }}>
                  {filteredBookings.length} réservations · {countParticipants(filteredBookings)} participants
                </div>
              </div>
            )}
          </div>
          );
        })
      )}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '10px 14px', background: '#2a2a2a',
  border: '1px solid #444', borderRadius: '10px', color: 'white',
  fontSize: '13px', outline: 'none'
};

export default Groups;
